import { languageDisplayCode } from '@/api/enums';
import {
  CampaignStatusesEnum,
  CampaignStatusMessages,
} from '@/client/campaigns';
import { User } from '@/client/users';
import { TranslationFunctionType } from '@/common/types';
import { useToast } from '@/hooks/useToast';
import { getEmailTypesLabels } from '@/utils/helpers/system-emails-configs';
import { t } from 'i18next';

export const emDash = '—';

export const getFirstNamePart = (name?: string): string =>
  (name ?? '').trim().split(/\s+/)[0] || '';

export const displayFullName = (
  person?: { firstName?: string | null; lastName?: string | null } | null,
): string => {
  const fullName = [person?.firstName, person?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();

  return fullName || emDash;
};

export const displayCreatedBy = (
  t: TranslationFunctionType,
  createdBy?: User | null,
) => {
  if (!createdBy) return t('generic.unkown');

  return `${createdBy.firstName} ${createdBy.lastName}`;
};

export const displaySenderProfile = (
  t: TranslationFunctionType,
  senderProfileName?: string,
) => {
  if (!senderProfileName) return t('generic.unkown');

  return t('campaign.template.createdByName', { name: senderProfileName });
};

export const displaySystemEmailTemplateName = (
  name: string,
  t: TranslationFunctionType,
) => {
  const [emailType, language] = name.split(' ');

  return `${getEmailTypesLabels(t)[emailType]}, ${
    languageDisplayCode[language]
  }`;
};

export const displayCampaignStatus = (
  t: TranslationFunctionType,
  status?: CampaignStatusesEnum,
) => {
  if (!status) return t('generic.unkown');

  return t(CampaignStatusMessages[status]);
};

const NAMED_HTML_ENTITIES: Record<string, string> = {
  amp: '&',
  gt: '>',
  lt: '<',
  quot: '"',
  apos: '\u0027',
  nbsp: ' ',
};

export const decodeHTML = (str: string): string =>
  str.replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z]+);/gi, (match, entity: string) => {
    if (entity.startsWith('#')) {
      const isHex = entity[1]?.toLowerCase() === 'x';
      const code = parseInt(entity.slice(isHex ? 2 : 1), isHex ? 16 : 10);

      return Number.isNaN(code) || code < 0 || code > 0x10ffff
        ? match
        : String.fromCodePoint(code);
    }

    return NAMED_HTML_ENTITIES[entity.toLowerCase()] ?? match;
  });

export const handleHandlebarsError = (
  e: any,
  toast: ReturnType<typeof useToast>,
) =>
  toast?.error(
    t('error'),
    t('error.invalidTemplate', { message: decodeHTML(e.message) }),
  );
